import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  OfficeNotificationPracticeAreaDto,
  OfficeNotificationSettingDto,
  OfficeNotificationTypeDto,
  UpsertOfficeNotificationPracticeAreaRequest,
  UpsertOfficeNotificationSettingRequest,
} from '../models/office-notification.models';

/**
 * Wraps the OfficeNotificationSettingsController — the management API for per-office scheduled
 * notifications. Reads/upserts an office's config and its per-practice-area toggles, plus the lookup
 * data (time zones, notification types) the config UI needs.
 */
@Injectable({
  providedIn: 'root',
})
export class OfficeNotificationSettingsService {
  private http = inject(HttpClient);

  private readonly apiUrl = `${environment.apiUrl}/api/officenotificationsettings`;

  /**
   * Get the scheduled-notification configs for an office, each with its practice-area toggles
   * (GET /api/officenotificationsettings/{officeCode}).
   */
  getByOffice(officeCode: string): Observable<OfficeNotificationSettingDto[]> {
    return this.http
      .get<OfficeNotificationSettingDto[]>(
        `${this.apiUrl}/${encodeURIComponent(officeCode)}`,
      );
  }

  /**
   * Create or update the config for an office + type (PUT /api/officenotificationsettings). Upsert
   * keyed by the (officeCode, type) natural key, so the auto-saving UI needn't track create vs update.
   */
  upsert(
    request: UpsertOfficeNotificationSettingRequest,
  ): Observable<OfficeNotificationSettingDto> {
    return this.http.put<OfficeNotificationSettingDto>(this.apiUrl, request);
  }

  /**
   * Enable or disable a practice area for an office notification setting
   * (PUT /api/officenotificationsettings/{settingId}/practice-areas/{practiceAreaCode}).
   */
  upsertPracticeArea(
    settingId: number,
    practiceAreaCode: string,
    enabled: boolean,
  ): Observable<OfficeNotificationPracticeAreaDto> {
    const body: UpsertOfficeNotificationPracticeAreaRequest = { enabled };
    return this.http
      .put<OfficeNotificationPracticeAreaDto>(
        `${this.apiUrl}/${settingId}/practice-areas/${encodeURIComponent(practiceAreaCode)}`,
        body,
      );
  }

  /** List the IANA time zone ids for the config UI's time zone dropdown. */
  getTimeZones(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/timezones`);
  }

  /** List the office-scheduled notification types available for configuration (with default days). */
  getTypes(): Observable<OfficeNotificationTypeDto[]> {
    return this.http.get<OfficeNotificationTypeDto[]>(`${this.apiUrl}/types`);
  }
}
